import { supabase } from '../config/supabaseConfig.ts';

const toISODate = (d) => d.toISOString().slice(0, 10);

export const today = () => toISODate(new Date());

export const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return toISODate(d);
};

export const startOfMonth = () => {
  const d = new Date();
  return toISODate(new Date(d.getFullYear(), d.getMonth(), 1));
};

export const startOfYear = () => {
  const d = new Date();
  return toISODate(new Date(d.getFullYear(), 0, 1));
};

// Rangos predefinidos para el selector de fechas
export const RANGE_PRESETS = {
  today: () => ({ start: today(), end: today() }),
  '7d':   () => ({ start: daysAgo(6), end: today() }),
  month: () => ({ start: startOfMonth(), end: today() }),
  year:  () => ({ start: startOfYear(), end: today() }),
};

// daily_cost_rollup.date se calcula en n8n/Postgres con `at time zone 'America/Mexico_City'`.
// Para filtrar conversation.created_at (timestamptz) por el mismo día "de negocio" hay que
// anclar el rango a ese mismo huso horario (CST fijo, México ya no usa horario de verano).
const MX_OFFSET = '-06:00';
const dayStartTs = (dateStr) => `${dateStr}T00:00:00${MX_OFFSET}`;
const dayEndTs = (dateStr) => `${dateStr}T23:59:59.999${MX_OFFSET}`;

export const costService = {
  // Mapa { project_id: { name, price } } para etiquetar y comparar contra mensualidad
  getProjectsInfo: async () => {
    const { data, error } = await supabase
      .from('project')
      .select('id, name, price, suscription');
    if (error) throw error;
    const map = {};
    (data || []).forEach(p => { map[p.id] = { name: p.name, price: p.price, suscription: p.suscription }; });
    return map;
  },

  // Costo agregado por proyecto entre dos fechas (inclusive), sumando daily_cost_rollup
  getSummaryByRange: async (startDate, endDate) => {
    const { data, error } = await supabase
      .from('daily_cost_rollup')
      .select('project_id, total_cost_usd, total_messages, total_prompt_tokens, total_completion_tokens')
      .gte('date', startDate)
      .lte('date', endDate);
    if (error) throw error;

    const grouped = {};
    (data || []).forEach(r => {
      if (!grouped[r.project_id]) {
        grouped[r.project_id] = {
          project_id: r.project_id,
          total_cost_usd: 0,
          total_messages: 0,
          total_prompt_tokens: 0,
          total_completion_tokens: 0,
        };
      }
      grouped[r.project_id].total_cost_usd += Number(r.total_cost_usd) || 0;
      grouped[r.project_id].total_messages += r.total_messages || 0;
      grouped[r.project_id].total_prompt_tokens += r.total_prompt_tokens || 0;
      grouped[r.project_id].total_completion_tokens += r.total_completion_tokens || 0;
    });
    return Object.values(grouped);
  },

  // Serie diaria de un proyecto para graficar (por defecto últimos 30 días)
  getDailySeries: async (projectId, startDate = daysAgo(29), endDate = today()) => {
    const { data, error } = await supabase
      .from('daily_cost_rollup')
      .select('date, total_cost_usd, total_messages')
      .eq('project_id', projectId)
      .gte('date', startDate)
      .lte('date', endDate)
      .order('date', { ascending: true });
    if (error) throw error;

    const byDate = {};
    (data || []).forEach(r => { byDate[r.date] = r; });

    // Rellena días sin actividad con 0 para que la gráfica no tenga huecos
    const series = [];
    const cursor = new Date(startDate + 'T00:00:00');
    const end = new Date(endDate + 'T00:00:00');
    while (cursor <= end) {
      const d = toISODate(cursor);
      const row = byDate[d];
      series.push({
        date: d,
        total_cost_usd: Number(row?.total_cost_usd) || 0,
        total_messages: row?.total_messages || 0,
      });
      cursor.setDate(cursor.getDate() + 1);
    }
    return series;
  },

  // Compara mensajes tipo='agent' totales del rango contra los que sí tienen costo
  // calculado (capture_metrics=true en n8n). Explica por qué "mensajes" del resumen
  // (que solo cuenta los que tienen costo) puede ser menor al total real de ejecuciones.
  getMessageCoverage: async (projectId, startDate, endDate) => {
    const gte = dayStartTs(startDate);
    const lte = dayEndTs(endDate);
    const [{ count: total, error: e1 }, { count: withCost, error: e2 }] = await Promise.all([
      supabase.from('conversation').select('id', { count: 'exact', head: true })
        .eq('project_id', projectId).eq('type', 'agent').gte('created_at', gte).lte('created_at', lte),
      supabase.from('conversation').select('id', { count: 'exact', head: true })
        .eq('project_id', projectId).eq('type', 'agent').not('cost_usd', 'is', null)
        .gte('created_at', gte).lte('created_at', lte),
    ]);
    if (e1) throw e1;
    if (e2) throw e2;
    return { total: total || 0, withCost: withCost || 0 };
  },

  // Detalle por ejecución (fila de conversation) para un proyecto y rango, paginado.
  getExecutionDetail: async (projectId, startDate, endDate, { limit = 50, offset = 0 } = {}) => {
    const gte = dayStartTs(startDate);
    const lte = dayEndTs(endDate);
    const { data, error, count } = await supabase
      .from('conversation')
      .select('id, created_at, model, prompt_tokens, completion_tokens, total_tokens, cost_usd, latency_ms, tool_call_count, execution_id, execution_status', { count: 'exact' })
      .eq('project_id', projectId)
      .eq('type', 'agent')
      .gte('created_at', gte)
      .lte('created_at', lte)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);
    if (error) throw error;
    return { rows: data || [], count: count || 0 };
  },
};
